import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { useAuth } from "../context/AuthContext";
import PageHeader from "../components/PageHeader";
import Modal from "../components/Modal";
import { authApi } from "../services/api";

function ChangePasswordModal({ show, onClose }) {
  const { user } = useApp();
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const close = () => {
    setSent(false);
    setError("");
    onClose();
  };

  const send = async () => {
    setError("");
    setBusy(true);
    try {
      await authApi.requestPasswordChange();
      setSent(true);
    } catch (err) {
      setError(err.message);
    }
    setBusy(false);
  };

  return (
    <Modal
      show={show}
      onClose={close}
      title="Change password"
      footer={
        <>
          <button type="button" className="btn btn-light" onClick={close}>
            {sent ? "Done" : "Cancel"}
          </button>
          {!sent && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={send}
              disabled={busy}
            >
              {busy ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Sending…
                </>
              ) : (
                <>
                  <i className="bi bi-envelope me-1" /> Email me a change link
                </>
              )}
            </button>
          )}
        </>
      }
    >
      {error && <div className="alert alert-danger py-2 small">{error}</div>}
      {sent ? (
        <div className="text-center">
          <i
            className="bi bi-envelope-check text-success"
            style={{ fontSize: 40 }}
          />
          <p className="small mt-3 mb-0">
            Check <strong>{user.email}</strong> - the link works for 1 hour and
            signs you out on your other devices. Didn't ask for this? That same
            email has a <strong>“This wasn't me”</strong> button that locks the
            account down.
          </p>
        </div>
      ) : (
        <>
          <p className="small">
            Passwords only change through your inbox, so a signed-in session
            alone is never enough. We'll email a one-time change link to:
          </p>
          <p className="fw-medium mb-2">{user.email}</p>
          <p className="small text-muted mb-0">
            The email also has a “This wasn't me” button - if you ever get one
            you didn't request, it signs your account out everywhere and cancels
            the link.
          </p>
        </>
      )}
    </Modal>
  );
}

function DeleteAccountModal({ show, onClose }) {
  const { user, logout } = useAuth();
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const close = () => {
    setPassword("");
    setError("");
    onClose();
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await authApi.deleteAccount(password);
      await logout();
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <Modal
      show={show}
      onClose={close}
      title="Delete your account?"
      footer={
        <>
          <button type="button" className="btn btn-light" onClick={close}>
            Cancel
          </button>
          <button
            type="submit"
            form="delete-account"
            className="btn btn-danger"
            disabled={busy || !password}
          >
            {busy && <span className="spinner-border spinner-border-sm me-2" />}
            Delete my account
          </button>
        </>
      }
    >
      <form id="delete-account" onSubmit={submit} noValidate>
        {error && <div className="alert alert-danger py-2 small">{error}</div>}
        <p className="small">
          This permanently deletes{" "}
          <span className="fw-medium">{user.email}</span> with your profile,
          jobs, documents, uploaded files and remembered answers, and signs out
          the Chrome extension. It can't be undone.
        </p>
        <label className="form-label small fw-medium" htmlFor="delete-password">
          Enter your password to confirm
        </label>
        <input
          id="delete-password"
          type="password"
          className="form-control"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoFocus
        />
      </form>
    </Modal>
  );
}

const SECTIONS = [
  {
    title: "Basic info",
    hint: "Filled into every application.",
    fields: [
      { key: "firstName", label: "First name", col: 6 },
      { key: "lastName", label: "Last name", col: 6 },
      {
        key: "email",
        label: "Email on applications",
        type: "email",
        col: 6,
        help: "What employers see. Your log-in email stays the same.",
      },
      { key: "phone", label: "Phone", type: "tel", col: 6 },
      {
        key: "location",
        label: "Location",
        placeholder: "City, State",
        col: 12,
      },
    ],
  },
];

// What's left of the paid features this user gets.
function PaidUsage({ integrations, role }) {
  const { access, limits, configured } = integrations;
  if (role === "superadmin") {
    return (
      <div className="small text-muted mt-1">
        Superadmins maintain Plateo from the Super Admin console and don&apos;t use job-seeker AI credits.
      </div>
    );
  }
  if (!access?.allowed) return null;
  if (!limits)
    return (
      <div className="small text-muted mt-1">
        As an admin, you have no limit on AI credits.
      </div>
    );
  const parts = [];
  if (configured.ai)
    parts.push(`${limits.ai.left} of ${limits.ai.total} AI credits left`);
  if (parts.length === 0) return null;
  return <div className="small text-muted mt-1">{parts.join(" · ")}</div>;
}

function Profile() {
  const {
    user,
    profile,
    updateProfile,
    toast,
    loadSampleData,
    startFresh,
    integrations,
  } = useApp();
  const { logout, setUser } = useAuth();
  const [form, setForm] = useState(profile);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);

  useEffect(() => setForm(profile), [profile]);

  // Returning from Google after connecting Gmail.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const result = params.get("gmail");
    if (!result) return;
    toast(
      result === "connected"
        ? "Gmail connected - outreach now sends from your own address"
        : "Gmail didn't connect. Try again.",
      result === "connected" ? "success" : "danger",
    );
    window.history.replaceState(null, "", window.location.pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connectGmail = async () => {
    try {
      const { url } = await authApi.gmailConnectUrl();
      window.location.href = url;
    } catch (err) {
      toast(err.message, "danger");
    }
  };

  const disconnectGmail = async () => {
    try {
      await authApi.disconnectGmail();
      const { user: fresh } = await authApi.me();
      setUser(fresh);
      toast("Gmail disconnected");
    } catch (err) {
      toast(err.message, "danger");
    }
  };

  const dirty = JSON.stringify(form) !== JSON.stringify(profile);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    if (await updateProfile(form)) toast("Profile saved");
    setBusy(false);
  };

  const runConfirmed = async () => {
    setBusy(true);
    const ok =
      confirm === "sample" ? await loadSampleData() : await startFresh();
    setBusy(false);
    setConfirm(null);
    if (ok)
      toast(
        confirm === "sample" ? "Sample jobs loaded" : "All data cleared",
        "info",
      );
  };

  const opsOnly = user?.role === "superadmin";

  return (
    <>
      <PageHeader
        title="Profile"
        subtitle={
          opsOnly
            ? "Account settings for the ops console."
            : "Your name and contact details, account, Gmail, Chrome extension and your data. What Platypus remembers about you lives in Memory."
        }
      />

      {!opsOnly && (
        <>
          <form onSubmit={save}>
            {SECTIONS.map((section) => (
              <div key={section.title} className="card mb-3">
                <div className="card-body">
                  <h2 className="h6 mb-0">{section.title}</h2>
                  <p className="small text-muted">{section.hint}</p>
                  <div className="row g-3">
                    {section.fields.map((f) => (
                      <div key={f.key} className={`col-md-${f.col}`}>
                        <label
                          className="form-label small fw-medium"
                          htmlFor={`p-${f.key}`}
                        >
                          {f.label}
                        </label>
                        {f.options ? (
                          <select
                            id={`p-${f.key}`}
                            className="form-select"
                            value={form[f.key] ?? ""}
                            onChange={(e) =>
                              setForm({ ...form, [f.key]: e.target.value })
                            }
                          >
                            {f.options.map((o) => (
                              <option key={o} value={o}>
                                {o || "Ask me"}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <input
                            id={`p-${f.key}`}
                            type={f.type || "text"}
                            className="form-control"
                            placeholder={f.placeholder}
                            value={form[f.key] ?? ""}
                            onChange={(e) =>
                              setForm({ ...form, [f.key]: e.target.value })
                            }
                          />
                        )}
                        {f.help && <div className="form-text">{f.help}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}

            <div className={`save-bar ${dirty ? "show" : ""}`}>
              <span className="small">You have unsaved changes</span>
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-sm btn-light"
                  onClick={() => setForm(profile)}
                >
                  Discard
                </button>
                <button
                  type="submit"
                  className="btn btn-sm btn-primary"
                  disabled={busy}
                >
                  Save profile
                </button>
              </div>
            </div>
          </form>

          <div className="card mt-4">
            <div className="card-body d-flex flex-wrap align-items-center gap-3">
              <div className="flex-grow-1">
                <h2 className="h6 mb-1">Platypus memory</h2>
                <div className="small text-muted">
                  Your links, answers to common questions, experiences, projects,
                  awards, patents and certifications - everything Platypus uses when
                  it tailors resumes, writes cover letters and answers application
                  questions - now lives on its own page.
                </div>
              </div>
              <Link to="/memory" className="btn btn-sm btn-outline-primary">
                <i className="bi bi-lightbulb me-1" /> Open Memory
              </Link>
            </div>
          </div>
        </>
      )}

      <div className={`card ${opsOnly ? "" : "mt-4"}`}>
        <div className="card-body d-flex flex-wrap align-items-center gap-3">
          <div className="flex-grow-1">
            <h2 className="h6 mb-1">Account</h2>
            <div className="small text-muted">
              Log-in email{" "}
              <span className="text-body fw-medium">{user.email}</span>
              <span className="tag tag-success ms-2">
                <i className="bi bi-check-circle" /> Confirmed
              </span>
            </div>
            <div className="small text-muted mt-1">
              Your log-in email can't be changed. To use a different one, create
              a new account with it.
            </div>
            <PaidUsage integrations={integrations} role={user?.role} />
          </div>
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => setChangingPassword(true)}
          >
            <i className="bi bi-key me-1" /> Change password
          </button>
          <button className="btn btn-sm btn-outline-secondary" onClick={logout}>
            <i className="bi bi-box-arrow-right me-1" /> Log out
          </button>
        </div>
      </div>

      {!opsOnly && (
        <>
          <div className="card mt-4">
            <div className="card-body d-flex flex-wrap align-items-center gap-3">
              <div className="flex-grow-1">
                <h2 className="h6 mb-1">Gmail for outreach</h2>
                {user.gmail?.connected ? (
                  <div className="small text-muted">
                    Connected as{" "}
                    <span className="text-body fw-medium">{user.gmail.email}</span>
                    <span className="tag tag-success ms-2">
                      <i className="bi bi-check-circle" /> Sending from your Gmail
                    </span>
                    <div className="mt-1">
                      Emails you send from a job go out as you, resume attached.
                      Plateo can only send - it can never read your inbox.
                    </div>
                  </div>
                ) : (
                  <div className="small text-muted">
                    Connect your Gmail so outreach emails come{" "}
                    <strong>from your own address</strong> (replies land in your
                    inbox, and your resume can be attached). Emails only ever send
                    from your own Gmail - Plateo never sends outreach from its own
                    address. Plateo only ever gets permission to send - never to
                    read your mail.
                  </div>
                )}
              </div>
              {user.gmail?.connected ? (
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={disconnectGmail}
                >
                  <i className="bi bi-google me-1" /> Disconnect
                </button>
              ) : integrations.configured?.gmail ? (
                <button className="btn btn-sm btn-primary" onClick={connectGmail}>
                  <i className="bi bi-google me-1" /> Connect Gmail
                </button>
              ) : (
                <span className="small text-muted">
                  Not set up on this server yet
                </span>
              )}
            </div>
          </div>

          <div className="card mt-4">
            <div className="card-body d-flex flex-wrap align-items-center gap-3">
              <div className="flex-grow-1">
                <h2 className="h6 mb-1">Chrome extension</h2>
                <div className="small text-muted">
                  Save jobs from any site and fill in applications with one click.
                </div>
              </div>
              <Link
                to="/connect-extension"
                className="btn btn-sm btn-outline-primary"
              >
                <i className="bi bi-puzzle me-1" /> Set up
              </Link>
            </div>
          </div>
        </>
      )}

      <div className="card mt-4">
        <div className="card-body d-flex flex-wrap align-items-center gap-3">
          <div className="flex-grow-1">
            <h2 className="h6 mb-1">Your data</h2>
            <div className="small text-muted">
              Delete your account and everything in it whenever you like. See
              the <Link to="/privacy">privacy policy</Link> for what Plateo
              keeps and who it's shared with, and the{" "}
              <Link to="/terms">terms of service</Link>.
            </div>
          </div>
          <button
            className="btn btn-sm btn-outline-danger"
            onClick={() => setDeletingAccount(true)}
          >
            <i className="bi bi-trash me-1" /> Delete account
          </button>
        </div>
      </div>

      <ChangePasswordModal
        show={changingPassword}
        onClose={() => setChangingPassword(false)}
      />
      <DeleteAccountModal
        show={deletingAccount}
        onClose={() => setDeletingAccount(false)}
      />

      <Modal
        show={!!confirm}
        onClose={() => setConfirm(null)}
        title={confirm === "sample" ? "Load sample jobs?" : "Start fresh?"}
        footer={
          <>
            <button className="btn btn-light" onClick={() => setConfirm(null)}>
              Cancel
            </button>
            <button
              className="btn btn-danger"
              onClick={runConfirmed}
              disabled={busy}
            >
              {busy && (
                <span className="spinner-border spinner-border-sm me-2" />
              )}
              {confirm === "sample" ? "Replace my jobs" : "Delete everything"}
            </button>
          </>
        }
      >
        <p className="mb-0">
          {confirm === "sample"
            ? "This replaces your jobs with a set of sample ones so you can explore. Your profile and documents stay."
            : "This deletes all jobs, contacts, documents and resets your profile. Your account stays. This can't be undone."}
        </p>
      </Modal>
    </>
  );
}

export default Profile;
